import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Dropdown,
  Grid,
  Header,
  Icon,
  Label,
  Segment,
  Table,
} from 'semantic-ui-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import axios from 'axios';
import { UserContext } from '../../context/User';
import './Dashboard.css';

const chartConfig = {
  lineChart: {
    line: { strokeWidth: 2, dot: false, activeDot: { r: 4 } },
    grid: { vertical: false, horizontal: true, opacity: 0.1 },
  },
  colors: {
    requests: '#4318FF',
    quota: '#00B5D8',
    tokens: '#6C63FF',
  },
  barColors: [
    '#4318FF', '#00B5D8', '#6C63FF', '#05CD99', '#FFB547',
    '#FF5E7D', '#41B883', '#7983FF', '#FF8F6B', '#49BEFF',
    '#E040FB', '#FF6E40', '#00E5FF', '#76FF03', '#FFAB40',
  ],
};

const dayOptions = [
  { key: 7, text: '7 天', value: 7 },
  { key: 14, text: '14 天', value: 14 },
  { key: 30, text: '30 天', value: 30 },
];

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
}

const Dashboard = () => {
  const { t } = useTranslation();
  const [userState] = useContext(UserContext);
  const isAdmin = userState?.user?.role >= 10;
  const screenWidth = useWindowWidth();
  const isMobile = screenWidth < 768;

  const [data, setData] = useState([]);
  const [adminData, setAdminData] = useState([]);
  const [userRanking, setUserRanking] = useState([]);
  const [days, setDays] = useState(7);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminDashboard(days);
      fetchUserRanking(days);
    }
  }, [isAdmin, days]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/api/user/dashboard');
      if (response.data.success) {
        setData(response.data.data || []);
      }
    } catch (error) {
      setData([]);
    }
  };

  const fetchAdminDashboard = async (d) => {
    try {
      const response = await axios.get(`/api/log/dashboard?days=${d}`);
      if (response.data.success) {
        setAdminData(response.data.data || []);
      }
    } catch (error) {
      setAdminData([]);
    }
  };

  const fetchUserRanking = async (d) => {
    try {
      const response = await axios.get(`/api/log/user_ranking?days=${d}&limit=20`);
      if (response.data.success) {
        setUserRanking(response.data.data || []);
      }
    } catch (error) {
      setUserRanking([]);
    }
  };

  const buildTimeSeries = useCallback((rawData, numDays) => {
    const dailyData = {};
    const maxDate = new Date();
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - (numDays - 1));

    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      dailyData[dateStr] = { date: dateStr, requests: 0, quota: 0, tokens: 0 };
    }

    rawData.forEach((item) => {
      if (dailyData[item.Day]) {
        dailyData[item.Day].requests += item.RequestCount;
        dailyData[item.Day].quota += item.Quota / 1000000;
        dailyData[item.Day].tokens += item.PromptTokens + item.CompletionTokens;
      }
    });

    return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
  }, []);

  const buildModelSeries = useCallback((rawData, numDays) => {
    const timeData = {};
    const maxDate = new Date();
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - (numDays - 1));
    const models = [...new Set(rawData.map((item) => item.ModelName))];

    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      timeData[dateStr] = { date: dateStr };
      models.forEach((m) => (timeData[dateStr][m] = 0));
    }

    rawData.forEach((item) => {
      if (timeData[item.Day]) {
        timeData[item.Day][item.ModelName] = item.PromptTokens + item.CompletionTokens;
      }
    });

    return { series: Object.values(timeData).sort((a, b) => a.date.localeCompare(b.date)), models };
  }, []);

  const buildModelQuotaSeries = useCallback((rawData, numDays) => {
    const timeData = {};
    const maxDate = new Date();
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - (numDays - 1));
    const models = [...new Set(rawData.map((item) => item.ModelName))];

    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      timeData[dateStr] = { date: dateStr };
      models.forEach((m) => (timeData[dateStr][m] = 0));
    }

    rawData.forEach((item) => {
      if (timeData[item.Day]) {
        timeData[item.Day][item.ModelName] = item.Quota / 1000000;
      }
    });

    return { series: Object.values(timeData).sort((a, b) => a.date.localeCompare(b.date)), models };
  }, []);

  const userTimeSeries = buildTimeSeries(data, 7);
  const adminTimeSeries = isAdmin ? buildTimeSeries(adminData, days) : [];
  const adminModelToken = isAdmin ? buildModelSeries(adminData, days) : { series: [], models: [] };
  const adminModelQuota = isAdmin ? buildModelQuotaSeries(adminData, days) : { series: [], models: [] };

  const getColor = (index) => chartConfig.barColors[index % chartConfig.barColors.length];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  const chartHeight = isMobile ? 200 : 280;
  const modelChartHeight = isMobile ? 240 : 320;
  const miniChartHeight = isMobile ? 100 : 120;

  const xAxisConfig = {
    dataKey: 'date',
    axisLine: false,
    tickLine: false,
    tick: { fontSize: isMobile ? 10 : 12, fill: '#A3AED0', textAnchor: 'middle' },
    tickFormatter: formatDate,
    interval: isMobile ? 'preserveStartEnd' : (days > 14 ? 'preserveStartEnd' : 0),
    minTickGap: isMobile ? 40 : 30,
    padding: { left: isMobile ? 10 : 20, right: isMobile ? 10 : 20 },
  };

  const tooltipStyle = {
    background: '#fff',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    padding: '8px 12px',
    fontSize: isMobile ? 12 : 14,
  };

  const totalAdminTokens = adminData.reduce((sum, item) => sum + item.PromptTokens + item.CompletionTokens, 0);
  const totalAdminQuota = adminData.reduce((sum, item) => sum + item.Quota, 0) / 1000000;
  const totalAdminRequests = adminData.reduce((sum, item) => sum + item.RequestCount, 0);

  const mobileRankColumns = isMobile
    ? ['rank', 'username', 'totalToken', 'quota']
    : ['rank', 'username', 'requests', 'prompt', 'completion', 'totalToken', 'quota'];

  return (
    <div className='dashboard-container'>
      {/* ========== 管理员全局面板 ========== */}
      {isAdmin && (
        <>
          <div className='dashboard-header'>
            <Header as='h3' style={{ margin: 0 }}>
              <Icon name='chart area' />
              运营总览
            </Header>
            <Dropdown
              selection
              compact
              options={dayOptions}
              value={days}
              onChange={(_, { value }) => setDays(value)}
              style={{ minWidth: isMobile ? 100 : 140 }}
            />
          </div>

          {/* 汇总卡片 */}
          <Grid columns={3} stackable style={{ marginBottom: 8 }}>
            <Grid.Column>
              <Segment className='summary-segment' style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
                <div className='summary-number'>{formatNumber(totalAdminRequests)}</div>
                <div className='summary-label'>总请求数</div>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment className='summary-segment' style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
                <div className='summary-number'>{formatNumber(totalAdminTokens)}</div>
                <div className='summary-label'>总 Token 消耗</div>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment className='summary-segment' style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' }}>
                <div className='summary-number'>${totalAdminQuota.toFixed(2)}</div>
                <div className='summary-label'>总消耗额度</div>
              </Segment>
            </Grid.Column>
          </Grid>

          {/* 全局趋势面积图 */}
          <Card fluid className='chart-card' style={{ marginBottom: 16 }}>
            <Card.Content>
              <Card.Header>请求量与 Token 消耗趋势</Card.Header>
              <div className='chart-container'>
                <ResponsiveContainer width='100%' height={chartHeight}>
                  <AreaChart data={adminTimeSeries}>
                    <defs>
                      <linearGradient id='colorReq' x1='0' y1='0' x2='0' y2='1'>
                        <stop offset='5%' stopColor='#4318FF' stopOpacity={0.15} />
                        <stop offset='95%' stopColor='#4318FF' stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id='colorTokens' x1='0' y1='0' x2='0' y2='1'>
                        <stop offset='5%' stopColor='#6C63FF' stopOpacity={0.15} />
                        <stop offset='95%' stopColor='#6C63FF' stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} opacity={0.08} />
                    <XAxis {...xAxisConfig} />
                    <YAxis axisLine={false} tickLine={false}
                      tick={{ fontSize: isMobile ? 10 : 11, fill: '#A3AED0' }}
                      tickFormatter={formatNumber}
                      width={isMobile ? 40 : 60}
                    />
                    <Tooltip contentStyle={tooltipStyle}
                      labelFormatter={(l) => `日期: ${formatDate(l)}`}
                      formatter={(v, name) => {
                        const labels = { requests: '请求数', tokens: 'Token 数', quota: '额度($)' };
                        return [name === 'quota' ? `$${v.toFixed(4)}` : formatNumber(v), labels[name] || name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 14 }}
                      formatter={(v) => ({ requests: '请求数', tokens: 'Token 数', quota: '额度($)' }[v] || v)}
                    />
                    <Area type='monotone' dataKey='requests' stroke='#4318FF' fill='url(#colorReq)' strokeWidth={2} />
                    <Area type='monotone' dataKey='tokens' stroke='#6C63FF' fill='url(#colorTokens)' strokeWidth={2} />
                    <Line type='monotone' dataKey='quota' stroke='#00B5D8' strokeWidth={2} dot={false} yAxisId={0} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card.Content>
          </Card>

          {/* 按模型维度图表 */}
          <Grid columns={isMobile ? 1 : 2} stackable>
            <Grid.Column>
              <Card fluid className='chart-card'>
                <Card.Content>
                  <Card.Header>各模型 Token 消耗趋势</Card.Header>
                  <div className='chart-container'>
                    <ResponsiveContainer width='100%' height={modelChartHeight}>
                      <LineChart data={adminModelToken.series}>
                        <CartesianGrid strokeDasharray='3 3' vertical={false} opacity={0.08} />
                        <XAxis {...xAxisConfig} />
                        <YAxis axisLine={false} tickLine={false}
                          tick={{ fontSize: isMobile ? 10 : 11, fill: '#A3AED0' }}
                          tickFormatter={formatNumber}
                          width={isMobile ? 40 : 60}
                        />
                        <Tooltip contentStyle={tooltipStyle}
                          labelFormatter={(l) => `日期: ${formatDate(l)}`}
                          formatter={(v) => [formatNumber(v), 'Token']}
                        />
                        <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 14 }} />
                        {adminModelToken.models.map((model, i) => (
                          <Line key={model} type='monotone' dataKey={model} stroke={getColor(i)} strokeWidth={2} dot={false} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Content>
              </Card>
            </Grid.Column>
            <Grid.Column>
              <Card fluid className='chart-card'>
                <Card.Content>
                  <Card.Header>各模型额度消耗趋势</Card.Header>
                  <div className='chart-container'>
                    <ResponsiveContainer width='100%' height={modelChartHeight}>
                      <BarChart data={adminModelQuota.series}>
                        <CartesianGrid strokeDasharray='3 3' vertical={false} opacity={0.08} />
                        <XAxis {...xAxisConfig} />
                        <YAxis axisLine={false} tickLine={false}
                          tick={{ fontSize: isMobile ? 10 : 11, fill: '#A3AED0' }}
                          width={isMobile ? 40 : 60}
                        />
                        <Tooltip contentStyle={tooltipStyle}
                          labelFormatter={(l) => `日期: ${formatDate(l)}`}
                          formatter={(v) => [`$${v.toFixed(4)}`, '额度']}
                        />
                        <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 14 }} />
                        {adminModelQuota.models.map((model, i) => (
                          <Bar key={model} dataKey={model} stackId='a' fill={getColor(i)}
                            radius={i === adminModelQuota.models.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>

          {/* 用户消耗排名 */}
          <Card fluid className='chart-card' style={{ marginTop: 16 }}>
            <Card.Content>
              <Card.Header>
                <Icon name='trophy' color='yellow' />
                用户消耗排名（Top 20）
              </Card.Header>
              <div className='ranking-table-wrapper'>
                <Table basic='very' selectable unstackable compact={isMobile ? 'very' : false} style={{ marginTop: 8 }}>
                  <Table.Header>
                    <Table.Row>
                      {mobileRankColumns.includes('rank') && <Table.HeaderCell textAlign='center'>排名</Table.HeaderCell>}
                      {mobileRankColumns.includes('username') && <Table.HeaderCell>用户</Table.HeaderCell>}
                      {mobileRankColumns.includes('requests') && <Table.HeaderCell textAlign='right'>请求</Table.HeaderCell>}
                      {mobileRankColumns.includes('prompt') && <Table.HeaderCell textAlign='right'>Prompt</Table.HeaderCell>}
                      {mobileRankColumns.includes('completion') && <Table.HeaderCell textAlign='right'>Completion</Table.HeaderCell>}
                      {mobileRankColumns.includes('totalToken') && <Table.HeaderCell textAlign='right'>Token</Table.HeaderCell>}
                      {mobileRankColumns.includes('quota') && <Table.HeaderCell textAlign='right'>额度</Table.HeaderCell>}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {userRanking.map((user, index) => (
                      <Table.Row key={user.user_id}>
                        {mobileRankColumns.includes('rank') && (
                          <Table.Cell textAlign='center'>
                            {index < 3 ? (
                              <Label circular color={['yellow', 'grey', 'brown'][index]} size='mini'>
                                {index + 1}
                              </Label>
                            ) : (
                              <span style={{ color: '#999', fontSize: 12 }}>{index + 1}</span>
                            )}
                          </Table.Cell>
                        )}
                        {mobileRankColumns.includes('username') && (
                          <Table.Cell>
                            <strong style={{ fontSize: isMobile ? 12 : 14 }}>
                              {user.username || `#${user.user_id}`}
                            </strong>
                          </Table.Cell>
                        )}
                        {mobileRankColumns.includes('requests') && (
                          <Table.Cell textAlign='right'>{user.request_count.toLocaleString()}</Table.Cell>
                        )}
                        {mobileRankColumns.includes('prompt') && (
                          <Table.Cell textAlign='right'>{user.prompt_tokens.toLocaleString()}</Table.Cell>
                        )}
                        {mobileRankColumns.includes('completion') && (
                          <Table.Cell textAlign='right'>{user.completion_tokens.toLocaleString()}</Table.Cell>
                        )}
                        {mobileRankColumns.includes('totalToken') && (
                          <Table.Cell textAlign='right'>
                            <strong>{formatNumber(user.prompt_tokens + user.completion_tokens)}</strong>
                          </Table.Cell>
                        )}
                        {mobileRankColumns.includes('quota') && (
                          <Table.Cell textAlign='right'>
                            <Label basic color='blue' size='mini'>
                              ${(user.total_quota / 1000000).toFixed(2)}
                            </Label>
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                    {userRanking.length === 0 && (
                      <Table.Row>
                        <Table.Cell colSpan={mobileRankColumns.length} textAlign='center' style={{ color: '#999', padding: 24 }}>
                          暂无数据
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </Card.Content>
          </Card>

          <Header as='h4' dividing style={{ marginTop: 24 }}>
            <Icon name='user' />
            个人使用统计
          </Header>
        </>
      )}

      {/* ========== 个人面板 ========== */}
      <Grid columns={isMobile ? 1 : 3} stackable className='charts-grid'>
        <Grid.Column>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Card.Header>{t('dashboard.charts.requests.title')}</Card.Header>
              <div className='chart-container'>
                <ResponsiveContainer width='100%' height={miniChartHeight}>
                  <LineChart data={userTimeSeries}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} horizontal opacity={0.1} />
                    <XAxis {...xAxisConfig} />
                    <YAxis hide />
                    <Tooltip contentStyle={tooltipStyle}
                      formatter={(v) => [v, t('dashboard.charts.requests.tooltip')]}
                      labelFormatter={(l) => `${t('dashboard.statistics.tooltip.date')}: ${formatDate(l)}`}
                    />
                    <Line type='monotone' dataKey='requests' stroke='#4318FF' strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Card.Header>{t('dashboard.charts.quota.title')}</Card.Header>
              <div className='chart-container'>
                <ResponsiveContainer width='100%' height={miniChartHeight}>
                  <LineChart data={userTimeSeries}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} horizontal opacity={0.1} />
                    <XAxis {...xAxisConfig} />
                    <YAxis hide />
                    <Tooltip contentStyle={tooltipStyle}
                      formatter={(v) => [v.toFixed(6), t('dashboard.charts.quota.tooltip')]}
                      labelFormatter={(l) => `${t('dashboard.statistics.tooltip.date')}: ${formatDate(l)}`}
                    />
                    <Line type='monotone' dataKey='quota' stroke='#00B5D8' strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Card.Header>{t('dashboard.charts.tokens.title')}</Card.Header>
              <div className='chart-container'>
                <ResponsiveContainer width='100%' height={miniChartHeight}>
                  <LineChart data={userTimeSeries}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} horizontal opacity={0.1} />
                    <XAxis {...xAxisConfig} />
                    <YAxis hide />
                    <Tooltip contentStyle={tooltipStyle}
                      formatter={(v) => [v, t('dashboard.charts.tokens.tooltip')]}
                      labelFormatter={(l) => `${t('dashboard.statistics.tooltip.date')}: ${formatDate(l)}`}
                    />
                    <Line type='monotone' dataKey='tokens' stroke='#6C63FF' strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>{t('dashboard.statistics.title')}</Card.Header>
          <div className='chart-container'>
            <ResponsiveContainer width='100%' height={isMobile ? 220 : 300}>
              <BarChart data={buildModelSeries(data, 7).series}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} opacity={0.1} />
                <XAxis {...xAxisConfig} />
                <YAxis axisLine={false} tickLine={false}
                  tick={{ fontSize: isMobile ? 10 : 12, fill: '#A3AED0' }}
                  width={isMobile ? 40 : 60}
                />
                <Tooltip contentStyle={tooltipStyle}
                  labelFormatter={(l) => `${t('dashboard.statistics.tooltip.date')}: ${formatDate(l)}`}
                />
                <Legend wrapperStyle={{ paddingTop: '12px', fontSize: isMobile ? 11 : 14 }} />
                {buildModelSeries(data, 7).models.map((model, index) => (
                  <Bar key={model} dataKey={model} stackId='a' fill={getColor(index)} name={model} radius={[4, 4, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default Dashboard;
